import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { TrendingUp } from 'lucide-react';
import {
  ComposedChart, Bar, Line, XAxis, YAxis, Tooltip as RTooltip,
  ResponsiveContainer, CartesianGrid, Legend, BarChart,
} from 'recharts';

const COLORS = {
  claimed: '#3b82f6',
  advanced: '#10b981',
  passed: '#ef4444',
  rate: '#f59e0b',
  intake: '#64748b',
};

const tooltipStyle = {
  background: 'hsl(222, 47%, 9%)', border: '1px solid rgba(148,163,184,0.2)',
  borderRadius: 2, fontSize: 12,
};

export function DealflowTrends() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.getDealflowAnalytics().then((res) => setData(res.data)).catch(() => {});
  }, []);

  if (!data) return null;
  const { months, totals } = data;

  return (
    <div className="glass-card card-depth rounded-sm p-4 sm:p-5" data-testid="dealflow-trends">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-4">
        <h3 className="font-heading text-lg text-foreground flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-blue-400" /> Dealflow Trends
        </h3>
        <span className="text-xs text-muted-foreground">
          Last 12 months · {totals.claimed} claimed · {totals.advanced} advanced · {totals.passed} passed
          {totals.pass_rate != null && <> · <span className="text-amber-400 font-medium">{totals.pass_rate}% pass rate</span></>}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Screening decisions + pass rate */}
        <div className="lg:col-span-2" data-testid="dealflow-decisions-chart">
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">
            Screening Activity & Pass Rate
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <ComposedChart data={months} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
              <YAxis yAxisId="rate" orientation="right" domain={[0, 100]} hide />
              <RTooltip contentStyle={tooltipStyle}
                formatter={(v, name) => [name === 'Pass rate %' && v != null ? `${v}%` : v, name]} />
              <Legend wrapperStyle={{ fontSize: 11 }} iconSize={9} />
              <Bar dataKey="claimed" name="Claimed" fill={COLORS.claimed} radius={[2, 2, 0, 0]} maxBarSize={22} />
              <Bar dataKey="advanced" name="Advanced" fill={COLORS.advanced} radius={[2, 2, 0, 0]} maxBarSize={22} />
              <Bar dataKey="passed" name="Passed" fill={COLORS.passed} radius={[2, 2, 0, 0]} maxBarSize={22} />
              <Line yAxisId="rate" dataKey="pass_rate" name="Pass rate %" stroke={COLORS.rate}
                strokeWidth={2} dot={{ r: 3, fill: COLORS.rate }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* New deals intake (own scale — batch uploads dwarf everything else) */}
        <div data-testid="dealflow-intake-chart">
          <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-bold mb-2">
            New Deals Added
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={months} margin={{ top: 5, right: 5, left: -15, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.1)" />
              <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} interval={2} />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: '#94a3b8' }} tickLine={false} axisLine={false} />
              <RTooltip contentStyle={tooltipStyle} formatter={(v) => [v.toLocaleString(), 'New deals']} />
              <Bar dataKey="new_deals" name="New deals" fill={COLORS.intake} radius={[2, 2, 0, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
